import classnames from 'classnames';
import type { ChangeEvent } from 'react';
import { CSSProperties, FC, useCallback, useMemo, useState } from 'react';

export interface PercentFieldWithIncrProps<T extends boolean = false> {
  id: string;
  value?: number;
  onValueChange?: T extends true ? (val?: number) => void : (val: number) => void;
  step?: number;
  placeholder?: string;
  digits?: number;
  disabled?: boolean;
  alignRight?: boolean;
  classNames?: string;
  displayWithoutButtons?: boolean;
  style?: CSSProperties;
  isHighlighted?: string;

  canBeEmpty?: T;
  tabIndex?: number;
}

const EmptyButton: FC = () => {
  return (
    <button
      type="button"
      className="btn"
      style={{ lineHeight: 'normal', minWidth: '24px', pointerEvents: 'none' }}
    ></button>
  );
};

type AdjustButtonProps = {
  adjustText: string;
  btnColor?: string;
  type?: string;
  onClick: () => void;
  tabIndex?: number;
};

const AdjustButton: FC<AdjustButtonProps> = ({ adjustText, btnColor, type, onClick, tabIndex }) => {
  return (
    <button
      className={classnames('btn h-50', btnColor)}
      style={{ lineHeight: 'normal' }}
      type="button"
      onClick={onClick}
      data-e2e={`${type}-button`}
      tabIndex={tabIndex}
    >
      {adjustText}
    </button>
  );
};

export function CompactPercentFieldAdjuster<T extends boolean = false>({
  id,
  value,
  onValueChange,
  step = 0.1,
  placeholder,
  digits,
  disabled,
  alignRight,
  classNames,
  displayWithoutButtons = false,
  style,
  isHighlighted,
  canBeEmpty,
  tabIndex,
}: PercentFieldWithIncrProps<T>) {
  const [val, setVal] = useState<string | null>(null);

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setVal(e.target.value);
  }, []);

  const onInc = useCallback(() => {
    if (step !== undefined && value !== undefined) {
      onValueChange?.(Number(value) + step);
    }
  }, [onValueChange, step, value]);

  const onDec = useCallback(() => {
    if (step !== undefined && value !== undefined) {
      onValueChange?.(Number(value) - step);
    }
  }, [onValueChange, step, value]);

  const displayedValue = useMemo(() => {
    if (val !== null) {
      return val;
    }
    if (value === undefined) {
      return '';
    }
    const res = digits !== undefined ? Number(value).toFixed(digits) : value;
    return res.toString();
  }, [val, value, digits]);

  const onBlur = useCallback(() => {
    if (val !== null) {
      const parsed = parseFloat(val || '');
      if (!val && canBeEmpty === true) {
        onValueChange?.(undefined as any);
      } else if (!isNaN(parsed)) {
        onValueChange?.(parsed as any);
      }
      setVal(null);
    }
  }, [val, canBeEmpty, onValueChange]);
  return (
    <div className="input-group" data-e2e={id} style={style}>
      <input
        className={classnames(
          'form-control',
          classNames,
          alignRight && 'text-end',
          displayWithoutButtons && 'shadow-none',
          {
            'is-highlighted-success': isHighlighted === 'success',
            'is-highlighted-info': isHighlighted === 'info',
            'is-highlighted-turquoise': isHighlighted === 'turquoise',
          },
        )}
        data-e2e={id + '-input'}
        disabled={onValueChange === undefined}
        id={id}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={placeholder}
        type="text"
        value={displayedValue}
        tabIndex={tabIndex}
      />
      <span
        className={classnames('input-group-text', {
          'bg-success bg-opacity-40': isHighlighted === 'success',
          'bg-info bg-opacity-40': isHighlighted === 'info',
          'bg-turquoise-blue bg-opacity-40': isHighlighted === 'turquoise',
        })}
        style={displayWithoutButtons ? { backgroundColor: 'unset' } : undefined}
      >
        %
      </span>
      {!disabled && (
        <div className="btn-group btn-group-sm btn-group-vertical" role="group">
          {displayWithoutButtons ? (
            <EmptyButton />
          ) : (
            <AdjustButton
              onClick={onInc}
              adjustText="+"
              btnColor={classnames('m-0 px-1 py-0', {
                'btn-success': isHighlighted === 'success',
                'btn-info': isHighlighted === 'info',
                'btn-turquoise-blue': isHighlighted === 'turquoise',
                'btn-light': isHighlighted !== 'success' && isHighlighted !== 'info' && isHighlighted !== 'turquoise',
              })}
              type="plus"
              tabIndex={tabIndex}
            />
          )}
          {displayWithoutButtons ? (
            <EmptyButton />
          ) : (
            <AdjustButton
              onClick={onDec}
              adjustText="-"
              btnColor={classnames('m-0 px-1 py-0', {
                'btn-success': isHighlighted === 'success',
                'btn-info': isHighlighted === 'info',
                'btn-turquoise-blue': isHighlighted === 'turquoise',
                'btn-light': isHighlighted !== 'success' && isHighlighted !== 'info' && isHighlighted !== 'turquoise',
              })}
              type="minus"
              tabIndex={tabIndex}
            />
          )}
        </div>
      )}
    </div>
  );
}
